import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";

// ─── Google Fonts ───────────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap";
document.head.appendChild(fontLink);

// ─── CSS ─────────────────────────────────────────────────────────────────────
const css = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --charcoal: #1a1a1a;
    --cream: #f5f0e8;
    --gold: #c9a84c;
    --gold-light: #e8d5a3;
    --blush: #e8c4b8;
    --text-muted: #888;
    --white: #ffffff;
    --serif: 'Cormorant Garamond', Georgia, serif;
    --sans: 'DM Sans', sans-serif;
  }
  html {
    scroll-behavior: smooth;
  }

  body { font-family: var(--sans); background: var(--cream); color: var(--charcoal); }

  /* NAV */
  .nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 1.25rem 4rem;
    background: rgba(245, 240, 232, 0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(201, 168, 76, 0.2);
    transition: all 0.3s ease;
  }
  .nav.scrolled {
    padding: 0.9rem 4rem;
    background: rgba(26, 26, 26, 0.96);
  }
  .nav.scrolled .nav-logo { color: var(--gold); }
  .nav.scrolled .nav-links a { color: var(--cream); }
  .nav.scrolled .nav-links a:hover { color: var(--gold); }
  .nav-logo {
    font-family: var(--serif);
    font-size: 1.6rem;
    font-weight: 600;
    color: var(--charcoal);
    letter-spacing: 0.05em;
    text-decoration: none;
  }
  .nav-logo span { color: var(--gold); }
  .nav-links { display: flex; gap: 2.5rem; align-items: center; }
  .nav-links a {
    font-family: var(--sans); font-size: 0.85rem; font-weight: 400;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none; color: var(--charcoal);
    transition: color 0.2s;
  }
  .nav-links a:hover { color: var(--gold); }
  .nav-cta {
    background: var(--charcoal) !important; color: var(--cream) !important;
    padding: 0.55rem 1.4rem !important;
    border-radius: 2px !important;
    letter-spacing: 0.1em !important;
    transition: background 0.2s !important;
  }
  .nav-cta:hover { background: var(--gold) !important; color: var(--charcoal) !important; }

  /* HERO */
  .hero {
    min-height: 100vh;
    display: grid; grid-template-columns: 1fr 1fr;
    padding-top: 80px;
    overflow: hidden;
  }
  .hero-left {
    display: flex; flex-direction: column; justify-content: center;
    padding: 6rem 4rem 6rem 6rem;
    position: relative;
  }
  .hero-eyebrow {
    font-family: var(--sans); font-size: 0.75rem; font-weight: 500;
    letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold);
    margin-bottom: 1.5rem;
    display: flex; align-items: center; gap: 0.75rem;
  }
  .hero-eyebrow::before {
    content: ''; display: block; width: 2rem; height: 1px; background: var(--gold);
  }
  .hero-title {
    font-family: var(--serif); font-size: clamp(3rem, 5vw, 5.5rem);
    font-weight: 600; line-height: 1.05; color: var(--charcoal);
    margin-bottom: 2rem;
  }
  .hero-title em { font-style: italic; color: var(--gold); }
  .hero-subtitle {
    font-family: var(--sans); font-size: 1.05rem; font-weight: 300;
    line-height: 1.8; color: #444; max-width: 480px;
    margin-bottom: 3rem;
  }
  .hero-actions { display: flex; gap: 1rem; align-items: center; }
  .btn-primary {
    background: var(--charcoal); color: var(--cream);
    padding: 1rem 2.2rem; border: none; border-radius: 2px;
    font-family: var(--sans); font-size: 0.8rem; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase;
    cursor: pointer; transition: all 0.25s ease;
  }
  .btn-primary:hover { background: var(--gold); color: var(--charcoal); transform: translateY(-2px); }
  .btn-secondary {
    background: transparent; color: var(--charcoal);
    padding: 1rem 2.2rem; border: 1px solid var(--charcoal); border-radius: 2px;
    font-family: var(--sans); font-size: 0.8rem; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase;
    cursor: pointer; transition: all 0.25s ease;
  }
  .btn-secondary:hover { background: var(--charcoal); color: var(--cream); transform: translateY(-2px); }
  .hero-right {
    background: var(--charcoal);
    display: flex; align-items: center; justify-content: center;
    position: relative; overflow: hidden;
  }
  .hero-right::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 60% 40%, rgba(201,168,76,0.15) 0%, transparent 65%);
  }
  .hero-visual {
    position: relative; z-index: 1;
    width: 100%; height: 100%;
    display: flex; flex-direction: column;
    justify-content: center; align-items: center;
    gap: 1.5rem; padding: 4rem;
  }
  .stat-card {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(201,168,76,0.25);
    border-radius: 4px; padding: 1.5rem 2rem;
    width: 100%; max-width: 340px;
    transform: translateX(-20px);
    opacity: 0;
    animation: slideIn 0.6s ease forwards;
  }
  .stat-card:nth-child(1) { animation-delay: 0.3s; }
  .stat-card:nth-child(2) { animation-delay: 0.5s; transform: translateX(20px); }
  .stat-card:nth-child(3) { animation-delay: 0.7s; }
  @keyframes slideIn {
    to { opacity: 1; transform: translateX(0); }
  }
  .stat-label {
    font-family: var(--sans); font-size: 0.7rem; font-weight: 500;
    letter-spacing: 0.15em; text-transform: uppercase; color: var(--gold);
    margin-bottom: 0.5rem;
  }
  .stat-value {
    font-family: var(--serif); font-size: 2.4rem; font-weight: 600;
    color: var(--cream); line-height: 1;
  }
  .stat-desc {
    font-family: var(--sans); font-size: 0.8rem; color: rgba(245,240,232,0.5);
    margin-top: 0.3rem;
  }

  /* MARQUEE STRIP */
  .marquee-strip {
    background: var(--charcoal); color: var(--gold);
    padding: 0.9rem 0; overflow: hidden;
    border-top: 1px solid rgba(201,168,76,0.3);
  }
  .marquee-inner {
    display: flex; gap: 3rem;
    animation: marquee 28s linear infinite;
    white-space: nowrap;
  }
  .marquee-inner span {
    font-family: var(--sans); font-size: 0.75rem;
    font-weight: 400; letter-spacing: 0.15em; text-transform: uppercase;
    flex-shrink: 0;
  }
  .marquee-inner span.dot { color: rgba(201,168,76,0.4); }
  @keyframes marquee {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }

  /* HOW IT WORKS */
  .section { padding: 7rem 6rem; }
  .section-tag {
    font-family: var(--sans); font-size: 0.7rem; font-weight: 500;
    letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold);
    margin-bottom: 1rem;
  }
  .section-title {
    font-family: var(--serif); font-size: clamp(2.2rem, 4vw, 3.5rem);
    font-weight: 600; line-height: 1.15; color: var(--charcoal);
    margin-bottom: 1.5rem;
  }
  .section-title em { font-style: italic; }
  .section-sub {
    font-family: var(--sans); font-size: 1rem; font-weight: 300;
    color: #666; line-height: 1.8; max-width: 520px;
    margin-bottom: 4rem;
  }
  .steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; }
  .step-card {
    padding: 3rem 2.5rem;
    border: 1px solid rgba(26,26,26,0.1);
    position: relative; overflow: hidden;
    transition: all 0.3s ease;
  }
  .step-card:hover { background: var(--charcoal); }
  .step-card:hover .step-number { color: var(--gold); }
  .step-card:hover .step-title { color: var(--cream); }
  .step-card:hover .step-text { color: rgba(245,240,232,0.6); }
  .step-card:hover .step-line { background: var(--gold); }
  .step-number {
    font-family: var(--serif); font-size: 4rem; font-weight: 600;
    color: rgba(201,168,76,0.2); line-height: 1;
    margin-bottom: 1.5rem; transition: color 0.3s;
  }
  .step-line {
    width: 2rem; height: 2px; background: var(--gold);
    margin-bottom: 1.5rem; transition: background 0.3s;
  }
  .step-title {
    font-family: var(--serif); font-size: 1.4rem; font-weight: 600;
    color: var(--charcoal); margin-bottom: 0.75rem; transition: color 0.3s;
  }
  .step-text {
    font-family: var(--sans); font-size: 0.9rem; font-weight: 300;
    color: #666; line-height: 1.7; transition: color 0.3s;
  }

  /* WHO IT'S FOR */
  .audience-section {
    padding: 7rem 0;
    background: var(--charcoal);
    position: relative; overflow: hidden;
  }
  .audience-section::before {
    content: '';
    position: absolute; top: -40%; left: -10%;
    width: 600px; height: 600px; border-radius: 50%;
    background: radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%);
  }
  .audience-inner { padding: 0 6rem; position: relative; z-index: 1; }
  .audience-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; margin-top: 4rem; }
  .audience-card {
    padding: 3.5rem;
    border: 1px solid rgba(201,168,76,0.2);
    border-radius: 2px;
    position: relative;
  }
  .audience-card::after {
    content: '';
    position: absolute; top: 0; left: 0;
    width: 4px; height: 100%;
    background: var(--gold);
    border-radius: 2px 0 0 2px;
  }
  .audience-icon {
    font-family: var(--serif); font-size: 3rem; color: var(--gold);
    margin-bottom: 1.5rem; line-height: 1;
  }
  .audience-role {
    font-family: var(--sans); font-size: 0.7rem; font-weight: 500;
    letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold);
    margin-bottom: 0.75rem;
  }
  .audience-title {
    font-family: var(--serif); font-size: 1.8rem; font-weight: 600;
    color: var(--cream); margin-bottom: 1rem;
  }
  .audience-text {
    font-family: var(--sans); font-size: 0.9rem; font-weight: 300;
    color: rgba(245,240,232,0.6); line-height: 1.8; margin-bottom: 1.5rem;
  }
  .audience-list {
    list-style: none; display: flex; flex-direction: column; gap: 0.6rem;
  }
  .audience-list li {
    font-family: var(--sans); font-size: 0.85rem; color: rgba(245,240,232,0.7);
    display: flex; align-items: center; gap: 0.75rem;
  }
  .audience-list li::before {
    content: ''; display: block; width: 1rem; height: 1px; background: var(--gold); flex-shrink: 0;
  }

  /* CATEGORIES */
  .categories-section { padding: 7rem 6rem; background: var(--cream); }
  .categories-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem; margin-top: 3.5rem; }
  .category-card {
    padding: 2rem 1.5rem;
    border: 1px solid rgba(26,26,26,0.1);
    border-radius: 2px; text-align: center;
    cursor: pointer; transition: all 0.3s ease;
  }
  .category-card:hover {
    background: var(--charcoal); border-color: var(--charcoal);
    transform: translateY(-4px);
  }
  .category-card:hover .cat-icon { color: var(--gold); }
  .category-card:hover .cat-name { color: var(--cream); }
  .category-card:hover .cat-count { color: rgba(245,240,232,0.5); }
  .cat-icon { font-size: 1.8rem; margin-bottom: 1rem; transition: color 0.3s; }
  .cat-name {
    font-family: var(--serif); font-size: 1.1rem; font-weight: 600;
    color: var(--charcoal); margin-bottom: 0.4rem; transition: color 0.3s;
  }
  .cat-count {
    font-family: var(--sans); font-size: 0.8rem; color: var(--text-muted);
    transition: color 0.3s;
  }

  /* TESTIMONIALS */
  .testimonials-section { padding: 7rem 6rem; background: #f0ebe1; }
  .testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; margin-top: 3.5rem; }
  .testimonial-card {
    background: var(--white); padding: 2.5rem;
    border-radius: 2px; position: relative;
  }
  .testimonial-card::before {
    content: '\u201C';
    font-family: var(--serif); font-size: 6rem; font-weight: 600;
    color: var(--gold-light); position: absolute; top: -0.5rem; left: 1.5rem;
    line-height: 1;
  }
  .testimonial-text {
    font-family: var(--serif); font-size: 1.05rem; font-style: italic;
    line-height: 1.7; color: #333; margin-bottom: 1.5rem; position: relative; z-index: 1;
  }
  .testimonial-author { display: flex; align-items: center; gap: 1rem; }
  .author-avatar {
    width: 44px; height: 44px; border-radius: 50%;
    background: var(--charcoal); display: flex; align-items: center; justify-content: center;
    font-family: var(--sans); font-size: 0.8rem; font-weight: 500; color: var(--gold);
  }
  .author-name {
    font-family: var(--sans); font-size: 0.9rem; font-weight: 500; color: var(--charcoal);
  }
  .author-role {
    font-family: var(--sans); font-size: 0.8rem; color: var(--text-muted);
  }

  /* CTA */
  .cta-section {
    padding: 8rem 6rem; background: var(--charcoal);
    text-align: center; position: relative; overflow: hidden;
  }
  .cta-section::before {
    content: '';
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: 700px; height: 700px; border-radius: 50%;
    border: 1px solid rgba(201,168,76,0.1);
  }
  .cta-section::after {
    content: '';
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: 400px; height: 400px; border-radius: 50%;
    border: 1px solid rgba(201,168,76,0.15);
  }
  .cta-inner { position: relative; z-index: 1; }
  .cta-eyebrow {
    font-family: var(--sans); font-size: 0.7rem; font-weight: 500;
    letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold);
    margin-bottom: 1.5rem;
  }
  .cta-title {
    font-family: var(--serif); font-size: clamp(2.5rem, 5vw, 4.5rem);
    font-weight: 600; line-height: 1.1; color: var(--cream);
    margin-bottom: 1.5rem;
  }
  .cta-title em { font-style: italic; color: var(--gold); }
  .cta-sub {
    font-family: var(--sans); font-size: 1rem; font-weight: 300;
    color: rgba(245,240,232,0.6); max-width: 500px; margin: 0 auto 3rem;
    line-height: 1.8;
  }
  .cta-buttons { display: flex; gap: 1.25rem; justify-content: center; }
  .btn-gold {
    background: var(--gold); color: var(--charcoal);
    padding: 1.1rem 2.5rem; border: none; border-radius: 2px;
    font-family: var(--sans); font-size: 0.8rem; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase;
    cursor: pointer; transition: all 0.25s ease;
  }
  .btn-gold:hover { background: var(--gold-light); transform: translateY(-2px); }
  .btn-outline-light {
    background: transparent; color: var(--cream);
    padding: 1.1rem 2.5rem; border: 1px solid rgba(245,240,232,0.3); border-radius: 2px;
    font-family: var(--sans); font-size: 0.8rem; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase;
    cursor: pointer; transition: all 0.25s ease;
  }
  .btn-outline-light:hover { border-color: var(--gold); color: var(--gold); transform: translateY(-2px); }

  /* FOOTER */
  .footer {
    background: #111; color: rgba(245,240,232,0.5);
    padding: 4rem 6rem 2.5rem;
  }
  .footer-top {
    display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 4rem; margin-bottom: 3rem;
    padding-bottom: 3rem;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }
  .footer-brand-name {
    font-family: var(--serif); font-size: 1.8rem; font-weight: 600;
    color: var(--cream); margin-bottom: 1rem;
  }
  .footer-brand-name span { color: var(--gold); }
  .footer-tagline {
    font-family: var(--sans); font-size: 0.85rem; font-weight: 300;
    line-height: 1.7; color: rgba(245,240,232,0.4); max-width: 260px;
  }
  .footer-col-title {
    font-family: var(--sans); font-size: 0.7rem; font-weight: 500;
    letter-spacing: 0.15em; text-transform: uppercase; color: var(--gold);
    margin-bottom: 1.25rem;
  }
  .footer-links { list-style: none; display: flex; flex-direction: column; gap: 0.6rem; }
  .footer-links a {
    font-family: var(--sans); font-size: 0.85rem; font-weight: 300;
    color: rgba(245,240,232,0.5); text-decoration: none;
    transition: color 0.2s;
  }
  .footer-links a:hover { color: var(--gold); }
  .footer-bottom {
    display: flex; justify-content: space-between; align-items: center;
  }
  .footer-copy {
    font-family: var(--sans); font-size: 0.8rem; font-weight: 300;
  }
  .footer-legal { display: flex; gap: 2rem; }
  .footer-legal a {
    font-family: var(--sans); font-size: 0.8rem; color: rgba(245,240,232,0.35);
    text-decoration: none; transition: color 0.2s;
  }
  .footer-legal a:hover { color: var(--gold); }

  @media (max-width: 1024px) {
    .hero { grid-template-columns: 1fr; min-height: auto; }
    .hero-right { min-height: 400px; }
    .hero-left { padding: 4rem 2rem; }
    .section { padding: 5rem 2rem; }
    .steps-grid { grid-template-columns: 1fr; gap: 0; }
    .audience-inner { padding: 0 2rem; }
    .audience-grid { grid-template-columns: 1fr; gap: 2rem; }
    .categories-section { padding: 5rem 2rem; }
    .categories-grid { grid-template-columns: repeat(2, 1fr); }
    .testimonials-section { padding: 5rem 2rem; }
    .testimonials-grid { grid-template-columns: 1fr; }
    .cta-section { padding: 5rem 2rem; }
    .footer { padding: 3rem 2rem 2rem; }
    .footer-top { grid-template-columns: 1fr 1fr; gap: 2rem; }
    .nav { padding: 1rem 2rem; }
    .nav.scrolled { padding: 0.75rem 2rem; }
  }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
const marqueeItems = [
  "Hair Stylists", "•", "Nail Technicians", "•", "Estheticians", "•",
  "Makeup Artists", "•", "Salon Owners", "•", "Spa Managers", "•",
  "Lash Technicians", "•", "Barbers", "•", "Beauty Educators", "•",
  "Hair Stylists", "•", "Nail Technicians", "•", "Estheticians", "•",
  "Makeup Artists", "•", "Salon Owners", "•", "Spa Managers", "•",
  "Lash Technicians", "•", "Barbers", "•", "Beauty Educators", "•",
];

const steps = [
  { n: "01", title: "Create your profile", text: "Whether you're a salon owner posting roles or a stylist showcasing your portfolio, set up your profile in minutes." },
  { n: "02", title: "Connect with purpose", text: "Smart matching surfaces the right opportunities — roles that fit your skills, location, and career goals." },
  { n: "03", title: "Grow your craft", text: "From first interview to contract signed, RemboList supports every step of the hiring journey." },
];

const categories = [
  { icon: "✂️", name: "Hair & Styling", count: "240+ roles" },
  { icon: "💅", name: "Nails & Beauty", count: "180+ roles" },
  { icon: "🧖", name: "Skincare & Spa", count: "130+ roles" },
  { icon: "💄", name: "Makeup Artistry", count: "95+ roles" },
  { icon: "🪒", name: "Barbering", count: "70+ roles" },
  { icon: "👁️", name: "Lash & Brow", count: "85+ roles" },
  { icon: "🏫", name: "Beauty Education", count: "40+ roles" },
  { icon: "💼", name: "Salon Management", count: "55+ roles" },
];

const testimonials = [
  {
    text: "Within two weeks of posting, I had three qualified stylists applying. RemboList understands our industry in a way generic job boards simply don't.",
    name: "Amara Osei", role: "Salon Owner, Nairobi", initials: "AO"
  },
  {
    text: "I'd been freelancing for years but wanted something steady. RemboList matched me with a spa that fit my specialty perfectly. Life-changing.",
    name: "Fatima Al-Rashidi", role: "Esthetician", initials: "FA"
  },
  {
    text: "The quality of applicants was remarkable. Every candidate understood the beauty industry. We hired our head nail technician within a month.",
    name: "Chloe Mwangi", role: "Spa Director", initials: "CM"
  },
];

// ─── Component ───────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    // Inject styles
    const style = document.createElement("style");
    style.textContent = css;
    document.head.appendChild(style);
    // Scroll listener
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => {
      document.head.removeChild(style);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      {/* NAV */}
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <Link to="#" className="nav-logo">Rembo<span>List</span></Link>
        <div className="nav-links">
          <a href="#how">How It Works</a>
          {/* <a href="#roles">Browse Roles</a> */}
          <a href="#" className="nav-cta" onClick={e => { e.preventDefault(); navigate("/signin"); }}>
            Enter Portal
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-left">
          <div className="hero-eyebrow">The Beauty Industry Jobs Platform</div>
          <h1 className="hero-title">
            Where beauty<br />
            <em>talent meets</em><br />
            opportunity
          </h1>
          <p className="hero-subtitle">
            RemboList connects skilled professionals with the salons, spas,
            and studios that need them — purpose-built for the beauty industry.
          </p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={e => { e.preventDefault(); navigate("/dashboard"); }}>
              Find a Job
            </button>
            <button className="btn-secondary" onClick={e => { e.preventDefault(); navigate("/employer/dashboard"); }}>
              Post a Role
            </button>
          </div>
        </div>
        <div className="hero-right">
          <div className="hero-visual">
            <div className="stat-card">
              <div className="stat-label">Active Job Listings</div>
              <div className="stat-value">700+</div>
              <div className="stat-desc">roles across East Africa</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Verified Professionals</div>
              <div className="stat-value">800+</div>
              <div className="stat-desc">stylists, technicians & artists</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Successful Placements</div>
              <div className="stat-value">300+</div>
              <div className="stat-desc">hires made this year</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-strip">
        <div className="marquee-inner">
          {marqueeItems.map((item, i) => (
            <span key={i} className={item === "•" ? "dot" : ""}>{item}</span>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="section" id="how">
        <div className="section-tag">How It Works</div>
        <h2 className="section-title">Built for the way <em>beauty works</em></h2>
        <p className="section-sub">
          We understand the craft, the pace, and the culture of beauty businesses.
          That's why RemboList was built from the ground up for this industry.
        </p>
        <div className="steps-grid">
          {steps.map((s) => (
            <div key={s.n} className="step-card">
              <div className="step-number">{s.n}</div>
              <div className="step-line" />
              <div className="step-title">{s.title}</div>
              <div className="step-text">{s.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="audience-section">
        <div className="audience-inner">
          <div className="section-tag" style={{ color: "rgba(201,168,76,0.8)" }}>Who It's For</div>
          <h2 className="section-title" style={{ color: "var(--cream)" }}>
            Two sides of the same <em>beautiful</em> industry
          </h2>
          <div className="audience-grid">
            <div className="audience-card">
              <div className="audience-icon">✦</div>
              <div className="audience-role">For Employers</div>
              <div className="audience-title">Salon & Spa Owners</div>
              <p className="audience-text">
                Stop sorting through irrelevant CVs. RemboList connects you directly
                with verified professionals who have the specialisation your business needs.
              </p>
              <ul className="audience-list">
                <li>Post unlimited job listings</li>
                <li>Browse verified talent portfolios</li>
                <li>Filter by specialisation, location & availability</li>
                <li>Manage applications in one dashboard</li>
              </ul>
            </div>
            <div className="audience-card">
              <div className="audience-icon">◈</div>
              <div className="audience-role">For Job Seekers</div>
              <div className="audience-title">Beauty Professionals</div>
              <p className="audience-text">
                Showcase your skills, build your profile, and let the right employers
                come to you — from entry-level roles to senior management positions.
              </p>
              <ul className="audience-list">
                <li>Create a professional portfolio profile</li>
                <li>Get matched to roles that fit your skills</li>
                <li>Apply with one click to multiple positions</li>
                <li>Track every application in real-time</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="categories-section" id="roles">
        <div className="section-tag">Specialisations</div>
        <h2 className="section-title">Every discipline, <em>covered</em></h2>
        <div className="categories-grid">
          {categories.map((c) => (
            <div key={c.name} className="category-card">
              <div className="cat-icon">{c.icon}</div>
              <div className="cat-name">{c.name}</div>
              <div className="cat-count">{c.count}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials-section">
        <div className="section-tag">Success Stories</div>
        <h2 className="section-title">Trusted by the <em>beauty community</em></h2>
        <div className="testimonials-grid">
          {testimonials.map((t) => (
            <div key={t.name} className="testimonial-card">
              <p className="testimonial-text">{t.text}</p>
              <div className="testimonial-author">
                <div className="author-avatar">{t.initials}</div>
                <div>
                  <div className="author-name">{t.name}</div>
                  <div className="author-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-eyebrow">Get Started Today</div>
          <h2 className="cta-title">
            Your next chapter<br />
            starts <em>here</em>
          </h2>
          <p className="cta-sub">
            Whether you're building a team or building a career, RemboList
            is the platform the beauty industry has been waiting for.
          </p>
          <div className="cta-buttons">
            <button className="btn-gold" onClick={e => { e.preventDefault(); navigate("/dashboard"); }}>I'm Looking for Work</button>
            <button className="btn-outline-light" onClick={e => { e.preventDefault(); navigate("/employer/dashboard"); }}>I'm Hiring Talent</button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-top">
          <div>
            <div className="footer-brand-name">Beauty<span>Link</span></div>
            <p className="footer-tagline">
              The beauty industry's dedicated talent marketplace. Connecting craft with career across East Africa.
            </p>
          </div>
          <div>
            <div className="footer-col-title">Platform</div>
            <ul className="footer-links">
              <li><Link to="#">Browse Jobs</Link></li>
              <li><Link to="#">Post a Job</Link></li>
              <li><Link to="#">Talent Search</Link></li>
              <li><Link to="#">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <div className="footer-col-title">Company</div>
            <ul className="footer-links">
              <li><Link to="#">About Us</Link></li>
              <li><Link to="#">Blog</Link></li>
              <li><Link to="#">Careers</Link></li>
              <li><Link to="#">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="footer-col-title">Support</div>
            <ul className="footer-links">
              <li><Link to="#">Help Centre</Link></li>
              <li><Link to="#">For Employers</Link></li>
              <li><Link to="#">For Job Seekers</Link></li>
              <li><Link to="#">Safety</Link></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">© 2026 RemboList. All rights reserved.</div>
          <div className="footer-legal">
            <Link to="#">Privacy Policy</Link>
            <Link to="#">Terms of Service</Link>
            <Link to="#">Cookie Policy</Link>
          </div>
        </div>
      </footer>
    </>
  );
}
